import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { listApiKeys } from '../../services/userApi';
import ApiKeyManager from './ApiKeyManager';

/**
 * ApiKeyManagerProvider — app-wide state for the API Key Manager modal.
 *
 * Exposes `useApiKeyManager()` → { open, close, isOpen, keys, loading, refresh }.
 * Any component can call `useApiKeyManager().open()` to bring up the modal.
 *
 * Key list is cached here (not in the modal) so embedded cards like ApiKeyCard
 * can read from the same source of truth. Refresh happens on open, on any
 * mutation (create/rename/revoke — surfaced via `refresh()`), and on auth
 * changes.
 */

const ApiKeyManagerContext = createContext(null);

export const ApiKeyManagerProvider = ({ children }) => {
  const { currentUser } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Cached counts derived from the list so embedded cards can render without
  // refetching.
  const activeCount = keys.length;

  const refresh = useCallback(async () => {
    if (!currentUser) {
      setKeys([]);
      return;
    }
    try {
      setLoading(true);
      const { data } = await listApiKeys();
      setKeys(data?.keys || []);
      setHasLoadedOnce(true);
    } catch (err) {
      // Silent fail — embedded cards degrade gracefully, modal shows error inline
      console.error('Failed to list API keys:', err);
      setKeys([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Initial fetch when auth resolves
  useEffect(() => {
    if (currentUser) refresh();
    else setKeys([]);
  }, [currentUser, refresh]);

  const open = useCallback(() => {
    setIsOpen(true);
    // Always refresh on open — data may have changed since last load
    refresh();
  }, [refresh]);

  const close = useCallback(() => setIsOpen(false), []);

  // Value memoized so React only re-renders consumers on real changes
  const value = useMemo(
    () => ({
      isOpen,
      open,
      close,
      keys,
      activeCount,
      loading,
      hasLoadedOnce,
      refresh,
    }),
    [isOpen, open, close, keys, activeCount, loading, hasLoadedOnce, refresh],
  );

  return (
    <ApiKeyManagerContext.Provider value={value}>
      {children}
      <ApiKeyManager />
    </ApiKeyManagerContext.Provider>
  );
};

/**
 * Hook to access the API Key Manager from any component.
 * Returns { open, close, isOpen, keys, activeCount, loading, hasLoadedOnce, refresh }.
 */
export const useApiKeyManager = () => {
  const ctx = useContext(ApiKeyManagerContext);
  if (!ctx) {
    throw new Error('useApiKeyManager must be used inside <ApiKeyManagerProvider>');
  }
  return ctx;
};
